import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '../app-config.token';
import { AppConfig } from '../app-config';
import { AvailableOrderItemsDto } from '../models/dtos/orders/available-order-items-dto';
import { CreateOrderRequest } from '../contracts/requests/create-order-request';
import { PositionExpectationDto } from '../models/dtos/orders/position-expectation-dto';
import { Filter } from '../models/common/filter';
import { IPagedResult } from '../shared/models/PagedResult';
import { OrdersDto } from '../models/dtos/orders/orders-dto';
import { OrderStartRealizationItemsDto } from '../models/dtos/orders/order-start-realization-items-dto';
import { DictionaryItem } from '../models/dictionary-item';
import { EditWorkerRealizationDateRequest } from '../contracts/requests/orders/edit-worker-realization-date-request';
import { OrderRealizationItemsDto } from '../models/dtos/orders/order-realization-items-dto';
import { OrderItemToEditDto } from '../models/dtos/orders/order-item-to-edit-dto';
import { EditOrderItemRequest } from '../contracts/requests/orders/edit-order-item-request';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private http = inject(HttpClient);
  private config = inject<AppConfig>(APP_CONFIG);

  private readonly API_URL = this.config.resourceApiURI + '/orders';

  constructor() { }

  createOrder(orderRequest: CreateOrderRequest) {
    const url = `${this.API_URL}/create`;
    return this.http.post<number>(url, orderRequest);
  }

  getAvailableOrderItems(employerId: number, employerObjectId: number): Observable<AvailableOrderItemsDto[]> {
    const url = `${this.API_URL}/availableorderitems/${employerId}/${employerObjectId}`;

    return this.http.get<AvailableOrderItemsDto[]>(url);
  }

  getPositionExpectations(employerId?: number): Observable<PositionExpectationDto[]> {
    const url = `${this.API_URL}/positionExpectations`;

    let params = new HttpParams();
    if (!!employerId) {
      params = params.set('employerId', employerId.toString());
    }

    return this.http.get<PositionExpectationDto[]>(url, { params });
  }

  getOrders(page: number, count: number, sortingField: string, sortingDirection: string, filters: Filter[]): Observable<IPagedResult<OrdersDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('count', count.toString())
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    return this.http.get<IPagedResult<OrdersDto>>(this.API_URL, { params });
  }

  archiveOrderItem(id: number) {
    const url = `${this.API_URL}/${id}/archive`;
    return this.http.post(url, null);
  }

  restoreOrderItem(id: number) {
    const url = `${this.API_URL}/${id}/restore`;
    return this.http.post(url, null);
  }

  getStartRealizationItems(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string,
    filters: Filter[],
  ): Observable<IPagedResult<OrderStartRealizationItemsDto>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('count', count.toString())
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));

    return this.http.get<IPagedResult<OrderStartRealizationItemsDto>>(`${this.API_URL}/startRealizationList`, { params });
  }

  getAvailableEmployerObjects(name: string = ''): Observable<DictionaryItem[]> {
    const httpParams = new HttpParams().set('name', encodeURIComponent(name));

    return this.http.get<DictionaryItem[]>(`${this.API_URL}/availableEmployerObjects`, {
      params: httpParams,
    });
  }

  cleanWorkerRealizationDate(id: number) {
    const url = `${this.API_URL}/workerRealizationDate/${id}/clean`;
    return this.http.post(url, null);
  }

  editWorkerRealizationDate(req: EditWorkerRealizationDateRequest) {
    const url = `${this.API_URL}/workerRealizationDate/edit`;
    return this.http.post(url, req);
  }

  getRealizationItems(
    periodFrom: Date,
    periodTo: Date,
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string,
  ): Observable<IPagedResult<OrderRealizationItemsDto>> {
    let params = new HttpParams()
      .set('periodFrom', periodFrom.toISOString())
      .set('periodTo', periodTo.toISOString())
      .set('page', page.toString())
      .set('count', count.toString())
      .set('sortingDirection', sortingDirection)
      .set('sortingField', sortingField);

    return this.http.get<IPagedResult<OrderRealizationItemsDto>>(`${this.API_URL}/realizationList`, { params });
  }

  getOrderItemToEdit(orderItemId: number): Observable<OrderItemToEditDto> {
    const url = `${this.API_URL}/${orderItemId}/edit`;
    return this.http.get<OrderItemToEditDto>(url);
  }

  editOrderItem(orderItemId: number, req: EditOrderItemRequest) {
    const url = `${this.API_URL}/${orderItemId}/edit`;
    return this.http.post(url, req);
  }
}
